import React from "react";
import { processSteps } from "../data/mock";
import { ArrowRight } from "lucide-react";

const stepBadges = [
  "Understand",
  "Clarify",
  "Ideate",
  "Build",
  "Refine",
  "Deliver",
];

const HowIWork = () => {
  return (
    <section
      id="process"
      className="py-24 lg:py-32 border-t border-neutral-100 dark:border-neutral-800 bg-white dark:bg-neutral-950"
    >
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* LEFT — sticky header */}
          <aside className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <div className="text-[12px] uppercase tracking-[0.18em] text-neutral-400 dark:text-neutral-500">
                How I work
              </div>
              <h2 className="mt-4 text-[34px] sm:text-[44px] lg:text-[52px] leading-[1.05] tracking-[-0.03em] font-medium text-neutral-900 dark:text-neutral-50">
                My approach to{" "}
                <span
                  style={{ fontFamily: "'Segoe Script', 'Caveat', cursive" }}
                  className="font-normal text-neutral-600 dark:text-neutral-400"
                >
                  design.
                </span>
              </h2>
              <p className="mt-5 text-[15.5px] text-neutral-600 dark:text-neutral-400 leading-[1.65] max-w-[420px]">
                Six steps every time — same rhythm whether the brief is a Notion
                doc or a 600-frame Figma file already in motion.
              </p>

              {/* Step flow chips */}
              <div className="mt-8 flex flex-wrap items-center gap-x-1.5 gap-y-2">
                {stepBadges.map((b, i) => (
                  <React.Fragment key={b}>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-[12px] text-neutral-700 dark:text-neutral-300">
                      {b}
                    </span>
                    {i < stepBadges.length - 1 && (
                      <ArrowRight
                        className="w-3 h-3 text-neutral-400 dark:text-neutral-600"
                        strokeWidth={2}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* Quote block */}
              <div className="mt-10 hidden lg:block pl-5 border-l-2 border-neutral-200 dark:border-neutral-800">
                <p
                  className="text-[26px] lg:text-[30px] leading-[1.35] text-neutral-800 dark:text-neutral-200 tracking-[-0.005em]"
                  style={{ fontFamily: "'Segoe Script', 'Caveat', cursive" }}
                >
                  Every screen is a decision. Every decision is a flow. Every
                  flow has to ship.
                </p>
              </div>
            </div>
          </aside>

          {/* RIGHT — numbered editorial steps */}
          <div className="lg:col-span-7">
            <ol className="relative">
              {processSteps.map((step, idx) => (
                <li
                  key={step.step}
                  className="group relative pt-10 lg:pt-12 pb-10 lg:pb-12 border-t border-neutral-200 dark:border-neutral-800 first:border-t-0 first:pt-0"
                >
                  {/* Step header row */}
                  <div className="flex items-baseline gap-5">
                    <span className="text-[44px] lg:text-[56px] leading-[0.9] font-medium tracking-[-0.04em] text-neutral-900 dark:text-neutral-50 tabular-nums">
                      {step.step}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] uppercase tracking-[0.22em] text-neutral-400 dark:text-neutral-500">
                        {stepBadges[idx]}
                      </div>
                      <h3 className="mt-1.5 text-[22px] lg:text-[26px] font-medium leading-[1.2] tracking-[-0.015em] text-neutral-900 dark:text-neutral-50">
                        {step.title}
                      </h3>
                    </div>
                  </div>

                  {/* Questions list */}
                  <ul className="mt-6 lg:ml-[5.25rem] space-y-2">
                    {step.questions.map((q, i) => (
                      <li
                        key={q}
                        className="flex items-baseline gap-3 text-[15.5px] text-neutral-700 dark:text-neutral-300 leading-[1.55]"
                      >
                        <span className="shrink-0 text-[11px] font-mono text-neutral-400 dark:text-neutral-500 tabular-nums">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span>{q}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Body */}
                  <p className="mt-6 lg:ml-[5.25rem] text-[15px] text-neutral-600 dark:text-neutral-400 leading-[1.7] max-w-[600px]">
                    {step.body}
                  </p>

                  {/* Hover accent line — sits in the top border padding so it never touches the step number */}
                  <span
                    className="pointer-events-none absolute left-0 top-3 lg:top-4 h-px w-0 bg-neutral-900 dark:bg-neutral-50 transition-all duration-500 ease-out group-hover:w-12"
                    aria-hidden="true"
                  />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowIWork;
